import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { contentService } from '../supabase/contentService';

// Two separate contexts: data + refresh function
const ContentContext = createContext(null);
const RefreshContext = createContext(() => {});

// Minimal safe skeleton so pages never crash even when the DB is unreachable
const SKELETON = {
  company: { name: 'HASNAT', tagline: 'Packaging', fullName: 'HASNAT PACKAGING Private Limited', logo: 'HP', since: '2010', description: '' },
  navigation: [],
  home: {
    hero: { title: '', subtitle: '', backgroundImage: '', buttons: [] },
    aboutPreview: { label: '', title: '', button: { text: '', link: '/about' } },
    sections: [],
    services: { label: '', title: '', description: '', items: [] },
    projects: { label: '', title: '', items: [] },
    clients: { label: '', title: '', logos: [] }
  },
  services: {
    hero: { title: '', backgroundImage: '' },
    intro: { label: '', title: '' },
    details: [],
    allServices: { label: '', title: '', items: [] }
  },
  about: {
    hero: { title: '', backgroundImage: '' },
    intro: { label: '', title: '', content: [] },
    mission: { reverse: false, image: '', title: '', content: [] },
    stats: [],
    values: { reverse: true, image: '', title: '', content: [] },
    whyChooseUs: { label: '', title: '', items: [] }
  },
  careers: {
    hero: { title: '', backgroundImage: '' },
    intro: { label: '', title: '', content: '' },
    benefits: { title: 'Benefits', items: [] },
    culture: { reverse: false, image: '', title: '', content: [] },
    positions: { label: '', title: '', note: '', jobs: [] },
    application: { label: '', title: '', description: '' }
  },
  contact: {
    hero: { title: '', backgroundImage: '' },
    intro: { label: '', title: '', content: '' },
    contactInfo: [],
    form: {
      title: 'Send Us a Message',
      submitText: 'Send Message',
      fields: [
        { name: 'name',    label: 'Your Name',    type: 'text',     required: true },
        { name: 'email',   label: 'Email Address', type: 'email',    required: true },
        { name: 'phone',   label: 'Phone Number',  type: 'tel',      required: false },
        { name: 'company', label: 'Company Name',  type: 'text',     required: false },
        { name: 'message', label: 'Message',       type: 'textarea', required: true }
      ]
    }
  },
  footer: {
    about: { title: '', content: '' },
    quickLinks: { title: '', links: [] },
    services: { title: '', links: [] },
    social: { title: '', links: [] },
    copyright: ''
  }
};

export const ContentProvider = ({ children }) => {
  // Start with SKELETON so every page renders immediately — no loading spinner
  const [content, setContent] = useState(SKELETON);

  const loadContent = useCallback(async () => {
    try {
      const result = await contentService.getContent();
      if (result && result.data) {
        setContent(deepMerge(SKELETON, result.data));
      }
      // If no DB data, keep current state (SKELETON or last-saved)
    } catch (err) {
      console.error('Failed to load content from backend:', err);
      // Keep whatever content we already have — pages stay up
    }
  }, []);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  return (
    <RefreshContext.Provider value={loadContent}>
      <ContentContext.Provider value={content}>
        {children}
      </ContentContext.Provider>
    </RefreshContext.Provider>
  );
};

// Simple deep-merge: skeleton provides safe defaults, loaded data overrides
function deepMerge(skeleton, loaded) {
  if (!loaded || typeof loaded !== 'object' || Array.isArray(loaded)) return loaded ?? skeleton;
  const result = { ...skeleton };
  for (const key of Object.keys(loaded)) {
    if (
      loaded[key] !== null &&
      typeof loaded[key] === 'object' &&
      !Array.isArray(loaded[key]) &&
      skeleton[key] !== null &&
      typeof skeleton[key] === 'object' &&
      !Array.isArray(skeleton[key])
    ) {
      result[key] = deepMerge(skeleton[key], loaded[key]);
    } else {
      result[key] = loaded[key];
    }
  }
  return result;
}

export const useContent = () => useContext(ContentContext);

// Call this after CMS saves to immediately reflect changes on all pages
export const useRefreshContent = () => useContext(RefreshContext);

export default ContentContext;
